import React, { useState, useEffect, useRef } from 'react';
import { arAgingApi } from '../services/api';
import '../styles/Panel.css';

// Entities whose TB GL data is fetched from S3 (not SAP) and therefore require
// a Start Date input so the backend can resolve the S3 folder
// <start_date>_to_<reporting_date>/. Keep in sync with the backend check in
// ar_aging_service.py / ap_aging_service.py (_fetch_tb_gl_balances).
const ENTITIES_REQUIRING_START_DATE = ['everlife_holding_pte_ltd'];

// EL AR ageing buckets — must match AGEING_BUCKETS_EL in ar_aging_service.py.
// Drives the optional "Manual Bucket Adjustments" modal for EL group entities.
const AR_EL_BUCKETS = [
  'Unbilled',
  'Not Due',
  'Less than 6 months',
  '6 months to 1 year',
  '1-2 years',
  '2-3 years',
  'More than 3 years',
];

// TL group adjustment buckets — must match 'Unbilled' + AGEING_BUCKETS (TL) in the
// ageing services. The TL Summary by Ageing and FinAlyzer tab use these 12 names.
const TL_ADJUSTMENT_BUCKETS = [
  'Unbilled',
  'Not Due',
  '0-30 days',
  '30-60 days',
  '60-90 days',
  '90-120 days',
  '120-150 days',
  '150-180 days',
  '6 months to 1 year',
  '1-2 years',
  '2-3 years',
  'More than 3 years',
];

function ARAgingPanel({ entity }) {
  const [reportingDate, setReportingDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState('');
  const [useDateRange, setUseDateRange] = useState(false);

  // Whether to show a dedicated Start Date input — only for entities that pull TB from S3.
  const requiresStartDate = entity && ENTITIES_REQUIRING_START_DATE.includes(entity.name);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // TL-only toggle: when enabled, customers whose invoices net to within ±0.50
  // (base currency) are removed from the ageing/summaries/Finalyzer template
  // and moved to a separate "Netted Off" tab.
  const [excludeNetZero, setExcludeNetZero] = useState(false);

  // EL-only optional manual per-bucket adjustments. When the toggle is on and the
  // modal has values, a signed amount per bucket is sent and folded into the
  // Final Ageing Summary "Manual Adjustment" column + the Finalyzer total.
  const [enableBucketAdjustments, setEnableBucketAdjustments] = useState(false);
  const [showAdjustmentsModal, setShowAdjustmentsModal] = useState(false);
  const [bucketAdjustments, setBucketAdjustments] = useState({});

  // List of available AR ageing reports
  const [reportsList, setReportsList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);

  // Template upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Base currency and FX rates for manual entities
  const [baseCurrency, setBaseCurrency] = useState('');
  const [fxRatesFile, setFxRatesFile] = useState(null);
  const fxRatesFileInputRef = useRef(null);

  // Optional INR local-currency dump for TL SAP entities — when uploaded,
  // Advance-customer rows are written with the dump's INR Balance Due and
  // the Booking Rate cell becomes an Excel formula (INR / FC).
  const [localDumpFile, setLocalDumpFile] = useState(null);
  const localDumpFileInputRef = useRef(null);

  // Finalyzer push state (manual file upload)
  const [finalyzerFile, setFinalyzerFile] = useState(null);
  const finalyzerFileInputRef = useRef(null);
  const [finalyzerToDate, setFinalyzerToDate] = useState(new Date().toISOString().split('T')[0]);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pushError, setPushError] = useState(null);

  // Finalyzer push from S3 (auto-generated output)
  const [s3PushDate, setS3PushDate] = useState(new Date().toISOString().split('T')[0]);
  const [s3PushLoading, setS3PushLoading] = useState(false);
  const [s3PushResult, setS3PushResult] = useState(null);
  const [s3PushError, setS3PushError] = useState(null);
  const [downloadingFinalyzer, setDownloadingFinalyzer] = useState(null);

  // Template upload mode for SAP entities (show template upload option)
  const [useTemplateUpload, setUseTemplateUpload] = useState(false);
  const isTLEntity = entity && entity.connection_type && entity.connection_type.toLowerCase() === 'tl_sap_b1';
  const isELEntity = entity && entity.connection_type &&
    ['sql', 'analisa_sap_b1', 'lifeline_sap_b1', 'xero'].includes(entity.connection_type.toLowerCase());
  const isSAPEntity = isTLEntity || isELEntity;

  // EL group (lease_entity_group === 'EL') — the net-zero exclusion toggle is
  // offered for these entities only. Covers all EL connection types
  // (EL_SAP_B1, ANALISA_SAP_B1, CPC HANA, XERO, LIFELINE_SAP_B1, manual EL).
  const isELGroupEntity = entity && entity.lease_entity_group === 'EL';

  // TL group (lease_entity_group === 'TL') — same optional per-bucket manual
  // adjustment is offered, applied to the "RFS Ageing Summary" tab (RFS broad
  // buckets) and folded into the Finalyzer total. Net-zero exclusion stays EL-only.
  const isTLGroupEntity = entity && entity.lease_entity_group === 'TL';
  // Manual per-bucket adjustment is available for EL (Final Ageing Summary) and
  // TL (RFS Ageing Summary). Both use the same broad/RFS bucket set (AR_EL_BUCKETS).
  const showBucketAdjustments = isELGroupEntity || isTLGroupEntity;

  // Check if entity has no system access (connection_type: "N/A" or "MANUAL")
  const isManualUploadEntity = entity && entity.connection_type &&
    (entity.connection_type.toUpperCase() === 'N/A' || entity.connection_type.toUpperCase() === 'MANUAL');

  // Set default base currency from entity config when entity changes
  React.useEffect(() => {
    if (entity && entity.fx_rates && entity.fx_rates.base_currency) {
      setBaseCurrency(entity.fx_rates.base_currency);
    } else {
      setBaseCurrency('INR');
    }
  }, [entity]);

  // Load available AR ageing reports on component mount or entity change
  useEffect(() => {
    if (entity) {
      loadReportsList();
    }
  }, [entity]);

  const loadReportsList = async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await arAgingApi.listReports(entity.name);
      setReportsList(data.reports || []);
    } catch (err) {
      console.error('Error loading AR ageing reports list:', err);
      setListError(err.message);
    } finally {
      setListLoading(false);
    }
  };

  // Build the {bucket: number} payload from the modal inputs — drops blanks,
  // zeros, and non-numeric entries. Returns null when the feature is off, the
  // entity is non-EL, or nothing usable was entered, so the request is
  // byte-for-byte unchanged in the default case.
  const buildBucketAdjustments = () => {
    if (!showBucketAdjustments || !enableBucketAdjustments) return null;
    const out = {};
    Object.entries(bucketAdjustments).forEach(([bucket, val]) => {
      if (val === '' || val === null || val === undefined) return;
      const num = Number(val);
      if (!Number.isNaN(num) && num !== 0) out[bucket] = num;
    });
    return Object.keys(out).length > 0 ? out : null;
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Pass startDate only if date range mode is enabled
      const data = await arAgingApi.generateReport(
        entity.name,
        reportingDate,
        useDateRange ? startDate : null,
        // Only TL SAP entities use the local-dump override
        isTLEntity ? localDumpFile : null,
        // Net-zero customer exclusion — EL group entities only.
        isELGroupEntity ? excludeNetZero : false,
        // Optional manual per-bucket adjustments — EL (Final Ageing Summary) and
        // TL (RFS Ageing Summary) groups. Returns null otherwise (unchanged request).
        buildBucketAdjustments(),
      );
      setResult(data);
      // Reload the list to show the new report
      await loadReportsList();
    } catch (err) {
      setError(err.message || 'Failed to generate AR ageing report');
    } finally {
      setLoading(false);
    }
  };

  const handleLocalDumpFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setError('Only Excel files (.xlsx, .xls) are allowed for the local dump');
        setLocalDumpFile(null);
        if (localDumpFileInputRef.current) {
          localDumpFileInputRef.current.value = '';
        }
        return;
      }
      setLocalDumpFile(file);
      setError(null);
    } else {
      setLocalDumpFile(null);
    }
  };

  const handleClearLocalDump = () => {
    setLocalDumpFile(null);
    if (localDumpFileInputRef.current) {
      localDumpFileInputRef.current.value = '';
    }
  };

  const handleDownload = async (reporting_date, timestamp) => {
    setDownloadingFile(`${reporting_date}_${timestamp}`);
    try {
      const blob = await arAgingApi.downloadReport(
        entity.name,
        reporting_date,
        timestamp
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `AR_Ageing_${entity.name}_${reporting_date}_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading AR ageing report:', err);
      alert(`Error downloading file: ${err.message}`);
    } finally {
      setDownloadingFile(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      // Timestamp format: YYYYMMDD_HHMMSS
      const year = timestamp.substring(0, 4);
      const month = timestamp.substring(4, 6);
      const day = timestamp.substring(6, 8);
      const hour = timestamp.substring(9, 11);
      const minute = timestamp.substring(11, 13);
      const second = timestamp.substring(13, 15);
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    } catch {
      return timestamp;
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await arAgingApi.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AR_Ageing_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const handleDownloadFxTemplate = async () => {
    try {
      const blob = await arAgingApi.downloadFxTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AR_FX_Rates_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download FX rates template: ' + err.message);
    }
  };

  const handleFxRatesFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
        setError('Please select an Excel file (.xlsx or .xls)');
        setFxRatesFile(null);
        return;
      }
      setFxRatesFile(file);
      setError(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls'];
      const isValid = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

      if (!isValid) {
        setError('Please select an Excel file (.xlsx or .xls)');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUploadSimpleTemplate = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    if (!reportingDate) {
      setError('Please select a reporting date');
      return;
    }

    if (requiresStartDate && !startDate) {
      setError(`Start Date is required for ${entity.name} — it determines which S3 TB folder to read.`);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Use simple template upload
      const data = await arAgingApi.uploadTemplateSimple(
        entity.name, reportingDate, selectedFile,
        isManualUploadEntity ? baseCurrency : null,
        isManualUploadEntity ? fxRatesFile : null,
        // Local-dump override applies only to TL SAP entities
        isTLEntity ? localDumpFile : null,
        // start_date only sent for entities that fetch TB from S3
        requiresStartDate ? startDate : null,
        // Net-zero customer exclusion — EL group entities only.
        isELGroupEntity ? excludeNetZero : false,
        // Optional manual per-bucket adjustments — EL (Final Ageing Summary) and
        // TL (RFS Ageing Summary) groups. Returns null otherwise (unchanged request).
        buildBucketAdjustments(),
      );
      setResult(data);
      setSelectedFile(null);
      setFxRatesFile(null);
      setLocalDumpFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (fxRatesFileInputRef.current) {
        fxRatesFileInputRef.current.value = '';
      }
      if (localDumpFileInputRef.current) {
        localDumpFileInputRef.current.value = '';
      }
      // Reload the list to show the new report
      await loadReportsList();
    } catch (err) {
      console.error('Template upload error:', err);
      setError(err.message || 'Failed to upload template');
    } finally {
      setLoading(false);
    }
  };

  // --- Finalyzer Output (S3) Handlers ---
  const handleDownloadFinalyzerOutput = async (reportingDate) => {
    setDownloadingFinalyzer(reportingDate);
    try {
      const blob = await arAgingApi.downloadFinalyzerOutput(entity.name, reportingDate);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AR_Finalyzer_${entity.name}_${reportingDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(`Could not download Finalyzer output for ${reportingDate}: ${err.message}`);
    } finally {
      setDownloadingFinalyzer(null);
    }
  };

  const handlePushFromS3 = async (reportingDate) => {
    if (!reportingDate) {
      setS3PushError('Please select a reporting period');
      return;
    }
    if (!window.confirm(`Push AR Receivables to Finalyzer for period ${reportingDate}?\n\nThis will use the auto-generated output stored in S3.`)) {
      return;
    }
    setS3PushLoading(true);
    setS3PushError(null);
    setS3PushResult(null);
    try {
      const data = await arAgingApi.pushToFinalyzerFromS3(entity.name, reportingDate);
      setS3PushResult(data);
    } catch (err) {
      if (err.isApiError && err.data) {
        setS3PushError(err.data.error || err.message || 'Failed to push to Finalyzer');
      } else {
        setS3PushError(err.message || 'Failed to push to Finalyzer');
      }
    } finally {
      setS3PushLoading(false);
    }
  };

  // --- Finalyzer Push Handlers ---
  const handleDownloadFinalyzerTemplate = async () => {
    try {
      const blob = await arAgingApi.downloadFinalyzerTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AR_Finalyzer_Push_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setPushError('Failed to download Finalyzer template: ' + err.message);
    }
  };

  const handleFinalyzerFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
        setPushError('Please select an Excel file (.xlsx or .xls)');
        setFinalyzerFile(null);
        return;
      }
      setFinalyzerFile(file);
      setPushError(null);
    }
  };

  const handlePushToFinalyzer = async () => {
    if (!finalyzerFile) {
      setPushError('Please select a file to upload');
      return;
    }
    if (!finalyzerToDate) {
      setPushError('Please select a to_date');
      return;
    }
    if (!window.confirm('Are you sure you want to push AR Receivables data to Finalyzer?')) {
      return;
    }

    setPushLoading(true);
    setPushError(null);
    setPushResult(null);

    try {
      const data = await arAgingApi.pushToFinalyzer(entity.name, finalyzerToDate, finalyzerFile);
      setPushResult(data);
      setFinalyzerFile(null);
      if (finalyzerFileInputRef.current) {
        finalyzerFileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error pushing AR to Finalyzer:', err);
      if (err.isApiError && err.data) {
        setPushError(err.data.error || err.message || 'Failed to push to Finalyzer');
      } else {
        setPushError(err.message || 'Failed to push to Finalyzer');
      }
    } finally {
      setPushLoading(false);
    }
  };

  return (
    <div className="panel ar-aging-panel">
      <h2>AR Ageing Analysis - {entity.name}</h2>

      <div className="alert alert-info" style={{ marginBottom: '20px', backgroundColor: '#e3f2fd', borderColor: '#2196f3', color: '#1565c0' }}>
        <strong>Note:</strong> AR Ageing report generates detailed Accounts Receivable analysis with customer-wise outstanding invoices and ageing buckets (Not Due Yet, 0-30, 30-60, 60-90, 90-120, 120-150, 150-180, 180-365, 365-730, 730-1095, More than 1095 days).
        <br />
        <br />
        <strong>Supported ERP Types:</strong>
        <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
          <li><strong>TL_SAP_B1:</strong> Fully supported (SAP HANA)</li>
          <li><strong>EL_SAP_B1:</strong> Fully supported (SQL Server)</li>
          <li><strong>Manual/N/A:</strong> Template-based upload supported</li>
        </ul>
      </div>

      {isManualUploadEntity && (
        <div className="alert alert-warning" style={{ marginBottom: '20px', backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}>
          <strong>Manual Upload Entity:</strong> This entity has no direct system access. Please download the AR template, fill it with your data, and upload it below.
        </div>
      )}

      <div className="section">
        <h3>{isManualUploadEntity ? 'Upload AR Ageing Data' : 'Generate New AR Ageing Report'}</h3>

        <div className="form-group">
          <label>Reporting Date:</label>
          <input
            type="date"
            value={reportingDate}
            onChange={(e) => setReportingDate(e.target.value)}
            disabled={loading}
            className="form-control"
          />
        </div>

        {requiresStartDate && (
          <div className="form-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={loading}
              className="form-control"
            />
            <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
              Required for {entity.name}: defines the S3 TB folder
              (<code>&lt;start_date&gt;_to_&lt;reporting_date&gt;/</code>) the backend reads
              the trial-balance file from.
            </small>
          </div>
        )}

        {isELGroupEntity && (
          <div className="form-group" style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={excludeNetZero}
                onChange={(e) => setExcludeNetZero(e.target.checked)}
                disabled={loading}
                style={{ marginRight: '8px' }}
              />
              <span>Exclude net-zero customers (net balance ≤ 0.50)</span>
            </label>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginLeft: '24px' }}>
              When enabled, any customer whose invoices net to within ±0.50 (base currency) is removed
              from the ageing buckets, all summaries, and the Finalyzer template, and moved to a
              separate "Netted Off" tab for audit.
            </p>
          </div>
        )}

        {showBucketAdjustments && (
          <div className="form-group" style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={enableBucketAdjustments}
                onChange={(e) => setEnableBucketAdjustments(e.target.checked)}
                disabled={loading}
                style={{ marginRight: '8px' }}
              />
              <span>Add manual bucket adjustments</span>
            </label>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginLeft: '24px' }}>
              Optionally enter a signed amount per ageing bucket (e.g. -39 or 40).
              Each value adjusts that bucket's Final Balance via an "Adjustment" column
              {isTLGroupEntity ? ' on the Summary by Ageing' : ''} and flows into the Finalyzer total.
            </p>
            {enableBucketAdjustments && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowAdjustmentsModal(true)}
                disabled={loading}
                style={{ marginLeft: '24px', marginTop: '4px' }}
              >
                Set adjustments
                {buildBucketAdjustments() ? ` (${Object.keys(buildBucketAdjustments()).length} set)` : ''}
              </button>
            )}
          </div>
        )}

        {showBucketAdjustments && showAdjustmentsModal && (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            }}
            onClick={() => setShowAdjustmentsModal(false)}
          >
            <div
              style={{
                background: '#fff', borderRadius: '8px', padding: '20px',
                width: '440px', maxWidth: '90%', maxHeight: '80vh', overflowY: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0 }}>Manual Bucket Adjustments (AR)</h3>
              <p style={{ fontSize: '12px', color: '#666' }}>
                Enter a signed amount for any bucket. Blank or 0 = no adjustment. Negative reduces,
                positive increases that bucket's Final Balance.
              </p>
              {(isTLGroupEntity ? TL_ADJUSTMENT_BUCKETS : AR_EL_BUCKETS).map((bucket) => (
                <div
                  key={bucket}
                  style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
                >
                  <label style={{ flex: 1 }}>{bucket}</label>
                  <input
                    type="number"
                    step="any"
                    className="form-control"
                    style={{ width: '150px' }}
                    value={bucketAdjustments[bucket] ?? ''}
                    onChange={(e) =>
                      setBucketAdjustments((prev) => ({ ...prev, [bucket]: e.target.value }))
                    }
                    placeholder="0"
                  />
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setBucketAdjustments({})}
                >
                  Clear all
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowAdjustmentsModal(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {isManualUploadEntity ? (
          <>
            {/* Manual entities: Template upload only */}
            <div className="form-group">
              <label>Base Currency (for conversion):</label>
              <select
                value={baseCurrency}
                onChange={(e) => setBaseCurrency(e.target.value)}
                className="form-control"
                style={{ width: '150px' }}
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="MYR">MYR</option>
                <option value="SGD">SGD</option>
              </select>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                All amounts will be converted to this currency. Fill "Booking Rate" column in template for exchange rates.
              </p>
            </div>

            <div className="form-group">
              <button
                onClick={handleDownloadTemplate}
                disabled={loading}
                className="btn btn-secondary"
                style={{ marginBottom: '10px' }}
              >
                Download AR Template
              </button>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Download the template, fill with your data (include Booking Rate for foreign currency invoices), then upload below
              </p>
            </div>

            <div className="form-group">
              <label>Select Template File:</label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                accept=".xlsx,.xls"
                disabled={loading}
                className="form-control"
              />
              {selectedFile && (
                <div className="file-selected" style={{ marginTop: '10px', color: '#28a745' }}>
                  Selected: {selectedFile.name}
                </div>
              )}
            </div>

            {/* FX Rates file upload for currency conversion */}
            <div className="form-group" style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px', border: '1px solid #dee2e6' }}>
              <label style={{ fontWeight: 'bold' }}>FX Rates (for currency conversion):</label>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginBottom: '10px' }}>
                Upload FX rates file with Currency and Rate columns (e.g., USD = 84 means 1 USD = 84 {baseCurrency}).
                Required for foreign currency invoices.
              </p>
              <button
                onClick={handleDownloadFxTemplate}
                disabled={loading}
                className="btn btn-secondary"
                style={{ marginBottom: '10px', fontSize: '13px' }}
              >
                Download FX Rates Template
              </button>
              <input
                ref={fxRatesFileInputRef}
                type="file"
                onChange={handleFxRatesFileChange}
                accept=".xlsx,.xls"
                disabled={loading}
                className="form-control"
              />
              {fxRatesFile && (
                <div style={{ marginTop: '8px', color: '#28a745', fontSize: '13px' }}>
                  FX Rates: {fxRatesFile.name}
                </div>
              )}
            </div>

            <button
              onClick={handleUploadSimpleTemplate}
              disabled={loading || !selectedFile || !reportingDate}
              className="btn btn-primary"
              style={{ marginTop: '10px' }}
            >
              {loading ? 'Processing Template...' : 'Upload & Process Template'}
            </button>
          </>
        ) : (
          <>
            {/* For SAP entities (TL and EL), show option to use template */}
            {isSAPEntity && (
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={useTemplateUpload}
                    onChange={(e) => {
                      setUseTemplateUpload(e.target.checked);
                      if (!e.target.checked) setSelectedFile(null);
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  <span>📋 Use Template Upload (Alternative to SAP Query)</span>
                </label>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginLeft: '24px' }}>
                  Check this if you want to upload an Excel template instead of fetching from SAP
                </p>
              </div>
            )}

            {useTemplateUpload && isSAPEntity ? (
              <>
                {/* Template upload UI for TL entities */}
                <div className="form-group">
                  <button
                    onClick={handleDownloadTemplate}
                    disabled={loading}
                    className="btn btn-secondary"
                    style={{ marginBottom: '10px' }}
                  >
                    📥 Download AR Template (8 columns)
                  </button>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Download the template, fill with your data, then upload below
                  </p>
                </div>

                <div className="form-group">
                  <label>Select Template File:</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept=".xlsx,.xls"
                    disabled={loading}
                    className="form-control"
                  />
                  {selectedFile && (
                    <div className="file-selected" style={{ marginTop: '10px', color: '#28a745' }}>
                      ✓ Selected: {selectedFile.name}
                    </div>
                  )}
                </div>

                {isTLEntity && (
                  <div
                    className="form-group"
                    style={{
                      marginTop: '15px',
                      marginBottom: '15px',
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '5px',
                      border: '1px solid #dee2e6',
                    }}
                  >
                    <label style={{ fontWeight: 'bold' }}>
                      Local Currency Dump (INR) — Optional
                    </label>
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginBottom: '10px' }}>
                      Upload an Excel file with per-invoice INR balances. For each
                      matched <strong>Advance-customer invoice</strong> (customers
                      with negative total Balance Due), the dump's INR balance is
                      written into Balance Due (INR) and the Booking Rate cell
                      becomes an Excel formula{' '}
                      <code>=Balance Due (INR) / Balance Due (Invoice Currency)</code>.
                      Expected columns: <code>Customer Code</code>,{' '}
                      <code>Invoice No</code> / <code>Doc. No.</code>, and{' '}
                      <code>Balance Due (INR)</code> / <code>Balance Due</code>.
                      Leave empty to keep the SAP Booking Rate.
                    </p>
                    <input
                      ref={localDumpFileInputRef}
                      type="file"
                      onChange={handleLocalDumpFileChange}
                      accept=".xlsx,.xls"
                      disabled={loading}
                      className="form-control"
                    />
                    {localDumpFile && (
                      <div style={{ marginTop: '8px', color: '#28a745', fontSize: '13px' }}>
                        ✓ Local Dump: {localDumpFile.name}{' '}
                        <button
                          type="button"
                          onClick={handleClearLocalDump}
                          disabled={loading}
                          style={{
                            marginLeft: '10px',
                            padding: '2px 8px',
                            fontSize: '11px',
                            border: '1px solid #ccc',
                            background: '#fff',
                            borderRadius: '3px',
                            cursor: 'pointer',
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={handleUploadSimpleTemplate}
                  disabled={loading || !selectedFile || !reportingDate}
                  className="btn btn-primary"
                  style={{ marginTop: '10px' }}
                >
                  {loading ? 'Processing Template...' : '📤 Upload & Process Template'}
                </button>
              </>
            ) : (
              <>
                {isTLEntity && (
                  <div
                    className="form-group"
                    style={{
                      marginBottom: '15px',
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '5px',
                      border: '1px solid #dee2e6',
                    }}
                  >
                    <label style={{ fontWeight: 'bold' }}>
                      Local Currency Dump (INR) — Optional
                    </label>
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginBottom: '10px' }}>
                      Upload an Excel file with per-invoice INR balances. For each
                      matched <strong>Advance-customer invoice</strong> (customers
                      with negative total Balance Due), the dump's INR balance is
                      written into Balance Due (INR) and the Booking Rate cell
                      becomes an Excel formula{' '}
                      <code>=Balance Due (INR) / Balance Due (Invoice Currency)</code>.
                      Expected columns: <code>Customer Code</code>,{' '}
                      <code>Invoice No</code> / <code>Doc. No.</code>, and{' '}
                      <code>Balance Due (INR)</code> / <code>Balance Due</code>.
                      Leave empty to keep the SAP Booking Rate.
                    </p>
                    <input
                      ref={localDumpFileInputRef}
                      type="file"
                      onChange={handleLocalDumpFileChange}
                      accept=".xlsx,.xls"
                      disabled={loading}
                      className="form-control"
                    />
                    {localDumpFile && (
                      <div style={{ marginTop: '8px', color: '#28a745', fontSize: '13px' }}>
                        ✓ Local Dump: {localDumpFile.name}{' '}
                        <button
                          type="button"
                          onClick={handleClearLocalDump}
                          disabled={loading}
                          style={{
                            marginLeft: '10px',
                            padding: '2px 8px',
                            fontSize: '11px',
                            border: '1px solid #ccc',
                            background: '#fff',
                            borderRadius: '3px',
                            cursor: 'pointer',
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={handleGenerateReport}
                  disabled={loading || !reportingDate}
                  className="btn btn-primary"
                >
                  {loading ? 'Generating...' : 'Generate AR Ageing Report'}
                </button>
              </>
            )}
          </>
        )}

        {result && (
          <div className="alert alert-success" style={{ marginTop: '15px' }}>
            <strong>Success!</strong> {result.message || 'AR ageing report generated successfully'}
            <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
              <li>Entity: {result.entity_name}</li>
              <li>Reporting Date: {result.reporting_date}</li>
              <li>Total Invoices: {formatNumber(result.summary?.total_invoices || result.total_invoices)}</li>
              <li>Total Customers: {formatNumber(result.summary?.total_customers || result.total_customers)}</li>
              <li>Total Balance Due: {formatNumber(result.summary?.total_balance_due || result.total_balance_due)}</li>
              <li>Timestamp: {formatTimestamp(result.timestamp)}</li>
              <li>S3 Path: <code>{result.s3_path}</code></li>
            </ul>
          </div>
        )}

        {error && (
          <div className="alert alert-danger" style={{ marginTop: '15px' }}>
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>

      <div className="section" style={{ marginTop: '30px' }}>
        <h3>Available AR Ageing Reports</h3>

        {listLoading && <div className="loading">Loading reports...</div>}

        {listError && (
          <div className="alert alert-danger">
            <strong>Error loading reports:</strong> {listError}
          </div>
        )}

        {!listLoading && !listError && reportsList.length === 0 && (
          <div className="empty-state">
            <p>No AR ageing reports available yet. Generate your first report above.</p>
          </div>
        )}

        {!listLoading && reportsList.length > 0 && (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Reporting Date</th>
                  <th>Generated At</th>
                  <th>Total Invoices</th>
                  <th>Total Customers</th>
                  <th>Total Balance Due</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reportsList.map((report, index) => (
                  <tr key={index}>
                    <td>{formatDate(report.reporting_date)}</td>
                    <td>{formatTimestamp(report.timestamp)}</td>
                    <td>{report.metadata ? formatNumber(report.metadata.total_invoices) : 'N/A'}</td>
                    <td>{report.metadata ? formatNumber(report.metadata.total_customers) : 'N/A'}</td>
                    <td>{report.metadata ? formatNumber(report.metadata.total_balance_due) : 'N/A'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => handleDownload(report.reporting_date, report.timestamp)}
                        disabled={downloadingFile === `${report.reporting_date}_${report.timestamp}`}
                        className="btn btn-sm btn-primary"
                        style={{ marginRight: '6px' }}
                      >
                        {downloadingFile === `${report.reporting_date}_${report.timestamp}`
                          ? 'Downloading...'
                          : 'Download'}
                      </button>
                      <button
                        onClick={() => handleDownloadFinalyzerOutput(report.reporting_date)}
                        disabled={downloadingFinalyzer === report.reporting_date}
                        className="btn btn-sm btn-secondary"
                        style={{ marginRight: '6px' }}
                        title="Download auto-generated Finalyzer push template"
                      >
                        {downloadingFinalyzer === report.reporting_date
                          ? 'Downloading...'
                          : 'Finalyzer Output'}
                      </button>
                      <button
                        onClick={() => handlePushFromS3(report.reporting_date)}
                        disabled={s3PushLoading}
                        className="btn btn-sm btn-danger"
                        title="Push this period's Finalyzer output to Finalyzer API"
                      >
                        Push to Finalyzer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button
          onClick={loadReportsList}
          disabled={listLoading}
          className="btn btn-secondary"
          style={{ marginTop: '15px' }}
        >
          {listLoading ? 'Refreshing...' : 'Refresh List'}
        </button>
      </div>

      {/* Push to Finalyzer Section */}
      <div className="section" style={{ marginTop: '30px' }}>
        <h3>Push AR Receivables to Finalyzer</h3>

        <div className="alert alert-info" style={{ marginBottom: '15px', backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}>
          <strong>Finalyzer Push:</strong> Upload AR schedule data and push it to the Finalyzer API
          (postScheduleData endpoint with data_mnemonic: SAP-Receivables).
          <br />
          Download the template, fill with your data, then upload and push.
        </div>

        <div className="form-group">
          <button
            onClick={handleDownloadFinalyzerTemplate}
            disabled={pushLoading}
            className="btn btn-secondary"
            style={{ marginBottom: '10px' }}
          >
            Download Finalyzer AR Template
          </button>
        </div>

        <div className="form-group">
          <label>To Date:</label>
          <input
            type="date"
            value={finalyzerToDate}
            onChange={(e) => setFinalyzerToDate(e.target.value)}
            disabled={pushLoading}
            className="form-control"
          />
        </div>

        <div className="form-group">
          <label>Select Finalyzer Data File:</label>
          <input
            ref={finalyzerFileInputRef}
            type="file"
            onChange={handleFinalyzerFileChange}
            accept=".xlsx,.xls"
            disabled={pushLoading}
            className="form-control"
          />
          {finalyzerFile && (
            <div style={{ marginTop: '10px', color: '#28a745' }}>
              Selected: {finalyzerFile.name}
            </div>
          )}
        </div>

        <button
          onClick={handlePushToFinalyzer}
          disabled={pushLoading || !finalyzerFile || !finalyzerToDate}
          className="btn btn-primary"
          style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
        >
          {pushLoading ? 'Pushing to Finalyzer...' : 'Push AR to Finalyzer'}
        </button>

        {pushResult && (
          <div className="alert alert-success" style={{ marginTop: '15px' }}>
            <strong>Success!</strong> AR Receivables data pushed to Finalyzer.
            <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
              <li>Entity: {pushResult.entity_name}</li>
              <li>Company Code: {pushResult.company_code}</li>
              <li>To Date: {pushResult.to_date}</li>
              <li>Records Sent: {pushResult.records_sent}</li>
              <li>Data Mnemonic: {pushResult.data_mnemonic}</li>
            </ul>
          </div>
        )}

        {pushError && (
          <div className="alert alert-danger" style={{ marginTop: '15px' }}>
            <strong>Error:</strong> {pushError}
          </div>
        )}

        {/* ── Push from Auto-Generated S3 Output ── */}
        <hr style={{ margin: '24px 0' }} />
        <h4 style={{ marginBottom: '10px' }}>Push from Auto-Generated Output (S3)</h4>
        <div className="alert alert-info" style={{ marginBottom: '15px', backgroundColor: '#e8f5e9', borderColor: '#4caf50', color: '#2e7d32' }}>
          When an AR aging report is generated, a Finalyzer push template is automatically built
          from the <strong>Final Ageing Summary</strong> tab and stored in S3.
          Select a period below to download it or push it directly to Finalyzer — no file upload needed.
        </div>

        <div className="form-group">
          <label>Reporting Period:</label>
          <input
            type="date"
            value={s3PushDate}
            onChange={(e) => { setS3PushDate(e.target.value); setS3PushResult(null); setS3PushError(null); }}
            disabled={s3PushLoading}
            className="form-control"
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
          <button
            onClick={() => handleDownloadFinalyzerOutput(s3PushDate)}
            disabled={!s3PushDate || downloadingFinalyzer === s3PushDate}
            className="btn btn-secondary"
          >
            {downloadingFinalyzer === s3PushDate ? 'Downloading...' : 'Download Generated Output'}
          </button>
          <button
            onClick={() => handlePushFromS3(s3PushDate)}
            disabled={s3PushLoading || !s3PushDate}
            className="btn btn-primary"
            style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
          >
            {s3PushLoading ? 'Pushing...' : 'Push to Finalyzer'}
          </button>
        </div>

        {s3PushResult && (
          <div className="alert alert-success" style={{ marginTop: '15px' }}>
            <strong>Success!</strong> AR Receivables pushed to Finalyzer from generated output.
            <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
              <li>Entity: {s3PushResult.entity_name}</li>
              <li>Company Code: {s3PushResult.company_code}</li>
              <li>Reporting Date: {s3PushResult.reporting_date}</li>
              <li>Records Sent: {s3PushResult.records_sent}</li>
              <li>Data Mnemonic: {s3PushResult.data_mnemonic}</li>
            </ul>
          </div>
        )}

        {s3PushError && (
          <div className="alert alert-danger" style={{ marginTop: '15px' }}>
            <strong>Error:</strong> {s3PushError}
          </div>
        )}
      </div>
    </div>
  );
}

export default ARAgingPanel;
